import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Pagination from '../../Pagination';
import './AdminDashboard.css';
import DashboardLayout from './DashboardLayout';

const AddCompanyDetails = () => {
  const BASE_API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();

  const currentAdminCompanyPage = location.state?.currentAdminCompanyPage || 0;
  const currentAdminCompanyPageSize = location.state?.currentAdminCompanyPageSize || 6;

  const [companyData, setCompanyData] = useState([]);
  const [page, setPage] = useState(currentAdminCompanyPage);
  const [pageSize, setPageSize] = useState(currentAdminCompanyPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 767);

  const isLastPage = page === totalPages - 1;
  const isPageSizeDisabled = isLastPage;

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_API_URL}/companiesList`, {
        params: { page, size: pageSize }
      });
      setCompanyData(response.data.content || []);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyByName = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_API_URL}/searchCompanies`, {
        params: {
          search,
          page,
          size: pageSize,
          sortOrder,
          sortBy: sortedColumn
        }
      });
      setCompanyData(response.data.content || []);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error searching for company:', error);
      alert('Error searching for company. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Unified fetch effect
  useEffect(() => {
    if (search.trim()) {
      fetchCompanyByName();
    } else {
      fetchCompanyData();
    }
  }, [search, page, pageSize, sortedColumn, sortOrder]);

  const handleSort = (column) => {
    let order = 'asc';
    if (sortedColumn === column) {
      order = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortedColumn(column);
    setSortOrder(order);
  };

  const handlePageClick = (data) => {
    setPage(data.selected);
  };

  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setPageSize(size);
    setPage(0); // Reset page
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0); // Reset page on new search
  };

  return (
    <DashboardLayout>
      <div className="main-content">
        <Row>
          <Col md={4}>
            <h2 className="text-start" style={{ marginLeft: '12px' }}>Add Company Details</h2>
          </Col>
          <Col md={4} className="d-flex align-items-left">
            <div className="search-bar" style={{ flex: 1 }}>
              <input
                style={{
                  borderColor: '#6c5b7b',
                  borderRadius: '6px',
                  height: '40px',
                  width: '100%',
                  padding: '0 10px'
                }}
                type="text"
                name="search"
                placeholder="Search by company name"
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </Col>
        </Row>

        {loading ? (
          <h4 className="text-center">Loading...</h4>
        ) : companyData.length > 0 ? (
          <div className="table-details-list table-wrapper">
            <Table hover className="text-center">
              <thead>
                <tr>
                  <th onClick={() => handleSort('companyName')}>Company Name</th>
                  <th>Company WebSite</th>
                  <th>Company Email</th>
                  <th>Service</th>
                  <th>Location</th>
                  <th>Submit Date</th>
                  <th>Status</th>
                  <th>Action Date</th>
                  <th>Add Detail</th>
                </tr>
              </thead>
              <tbody>
                {companyData.map((company) => (
                  <tr key={company.companyId}>
                    <td>{company.companyName}</td>
                    <td>{company.websiteLink}</td>
                    <td>{company.jobboxEmail}</td>
                    <td>{company.industryService}</td>
                    <td>{company.location}</td>
                    <td>{company.date}</td>
                    <td>{company.companyStatus}</td>
                    <td>{company.actionDate}</td>
                    <td>
                      <Link
                        to={{
                          pathname: '/admin-dashboard/companyDetailsByAdmin',
                          state: {
                            companyName: company.companyName,
                            currentAdminCompanyPage: page,
                            currentAdminCompanyPageSize: pageSize
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/admin-dashboard/companyDetailsByAdmin', {
                            state: {
                              companyName: company.companyName,
                              currentAdminCompanyPage: page,
                              currentAdminCompanyPageSize: pageSize
                            }
                          });
                        }}
                      >
                        ADD
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : search ? (
          <div className="text-center mt-4">
            <h4>No company found with the name "{search}"</h4>
          </div>
        ) : (
          <h4 className="text-center">No data available.</h4>
        )}

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          handlePageSizeChange={handlePageSizeChange}
          isPageSizeDisabled={isPageSizeDisabled}
          handlePageClick={handlePageClick}
        />
      </div>
    </DashboardLayout>
  );
};

export default AddCompanyDetails;
